import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';

import { useGeoMarkupStore } from '../store';
import {
  VECTOR_3D_STYLE,
  TERRAIN_SOURCE_ID,
  TERRAIN_DEM_SOURCE,
  TERRAIN_3D_PITCH,
  TERRAIN_3D_EXAGGERATION,
  TERRAIN_TRANSITION_MS,
} from '../config/maplibre';
import { useBasemapStyle } from './useBasemapStyle';

/**
 * Manages 3D terrain mode on a MapLibre map instance.
 *
 * Returns the current map style (OSM raster for 2D, OFM vector for 3D)
 * so the caller can pass it declaratively to the Map component.
 *
 * Imperatively manages terrain source, exaggeration, and pitch animation
 * via style.load callback after react-maplibre applies the new style.
 */
export function useTerrainMode(
  mapRef: RefObject<MapRef | null>,
  isMapReady: boolean
) {
  const is3DMode = useGeoMarkupStore((state) => state.map.is3DMode);
  const basemapStyle = useBasemapStyle();
  const setTransitioning = useGeoMarkupStore(
    (state) => state.map.setTransitioning
  );
  const isInitialRender = useRef(true);

  // Terrain/pitch side-effects after style swap
  useEffect(() => {
    const mapWrapper = mapRef.current;
    if (!mapWrapper || !isMapReady) return;

    const mapInstance = mapWrapper.getMap();

    // Skip no-op transition on initial render when already in 2D
    if (isInitialRender.current) {
      isInitialRender.current = false;
      if (!is3DMode) return;
    }

    mapInstance.stop();
    setTransitioning(true);

    const applyTerrain = () => {
      if (is3DMode) {
        if (!mapInstance.getSource(TERRAIN_SOURCE_ID)) {
          mapInstance.addSource(TERRAIN_SOURCE_ID, TERRAIN_DEM_SOURCE);
        }
        mapInstance.setTerrain({
          source: TERRAIN_SOURCE_ID,
          exaggeration: TERRAIN_3D_EXAGGERATION,
        });
        mapInstance.easeTo({
          pitch: TERRAIN_3D_PITCH,
          duration: TERRAIN_TRANSITION_MS,
        });
      } else {
        mapInstance.setTerrain(null);
        mapInstance.easeTo({ pitch: 0, duration: TERRAIN_TRANSITION_MS });
      }
    };

    mapInstance.once('style.load', applyTerrain);

    const timer = setTimeout(() => {
      setTransitioning(false);
    }, TERRAIN_TRANSITION_MS + 500);

    return () => {
      clearTimeout(timer);
      mapInstance.off('style.load', applyTerrain);
      mapInstance.stop();
      setTransitioning(false);
    };
  }, [is3DMode, isMapReady, mapRef, setTransitioning]);

  return is3DMode ? VECTOR_3D_STYLE : basemapStyle;
}
